/**
 * Array Utility Functions
 * Provides safe handling of potentially null/undefined arrays
 */

/**
 * Safely converts any value to an array, handling null/undefined
 * @param arr - The array to safely convert
 * @returns A guaranteed array (empty if input was null/undefined)
 */
export function safeArray<T>(arr?: T[] | null): T[] {
  return Array.isArray(arr) ? arr : [];
}

/**
 * Safely gets the length of an array, returning 0 for null/undefined
 * @param arr - The array to get length from
 * @returns The array length or 0 if null/undefined
 */
export function safeArrayLength<T>(arr?: T[] | null): number {
  return Array.isArray(arr) ? arr.length : 0;
}

/**
 * Safely checks if an array has elements
 * @param arr - The array to check
 * @returns True if array has elements, false if empty/null/undefined
 */
export function hasElements<T>(arr?: T[] | null): boolean {
  return Array.isArray(arr) && arr.length > 0;
}

/**
 * Safe array filter that handles null/undefined input
 * @param arr - The array to filter
 * @param predicate - The filter function
 * @returns Filtered array or empty array if input was null/undefined
 */
export function safeFilter<T>(arr: T[] | null | undefined, predicate: (item: T) => boolean): T[] {
  return safeArray(arr).filter(predicate);
}

/**
 * Safe array map that handles null/undefined input
 * @param arr - The array to map
 * @param mapFn - The mapping function
 * @returns Mapped array or empty array if input was null/undefined
 */
export function safeMap<T, U>(arr: T[] | null | undefined, mapFn: (item: T, index: number) => U): U[] {
  return safeArray(arr).map(mapFn);
}

/**
 * Extracts safe project arrays from a project object with explicit typing
 * Common pattern used across components
 */
export function extractProjectArrays(project: any) {
  return {
    tasks: safeArray(project?.tasks) as any[],
    images: safeArray(project?.images) as any[],
    sounds: safeArray(project?.sounds) as any[],
    annotations: safeArray(project?.annotations) as any[],
    notes: safeArray(project?.notes) as any[],
    transitions: safeArray(project?.transitions) as any[]
  };
}

/**
 * Typed version for when you know the specific type
 */
export function safeTypedArray<T>(arr: T[] | null | undefined): T[] {
  return Array.isArray(arr) ? arr : [];
}